import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication, Configuration } from '@azure/msal-browser';
import { ThemeProvider } from './contexts/ThemeContext';
import './App.css';

// Import pages
import Layout from './components/Layout';
import Home from './pages/Home';
import VolunteerRoster from './pages/VolunteerRoster';
import VolunteerList from './pages/VolunteerList';
import PatientCare from './pages/PatientCare';
import OrgChart from './pages/OrgChart';
import CalendarPage from './pages/Calendar';

import Login from './pages/Login';
import PrayerCommentBoxDemo from './components/PrayerCommentBoxDemo';
import TaskManagement from './pages/TaskManagement';
import DocumentManagement from './pages/DocumentManagement';

// Import auth context and protected route
import { AuthProvider, useAuth } from './hooks/useAuth';
import { SharePointProvider } from './hooks/useSharePoint';
import { ProtectedRoute } from './components/ProtectedRoute';

import { getAuthService } from './services/authService';
import { getSharePointService } from './services/sharePointService';
import { initializeSP } from './services/sharePointService';

// MSAL Configuration - Replace with your actual values
const getRedirectUri = () => {
  // Use the current window location to automatically detect the port
  return window.location.origin;
};

const getClientId = () => {
  // In production, you can set this via Azure Static Web Apps environment variables
  return process.env.REACT_APP_CLIENT_ID || '300d5411-9056-4d1b-ae47-02fec49703cc';
};

const getTenantId = () => {
  // In production, you can set this via Azure Static Web Apps environment variables
  return process.env.REACT_APP_TENANT_ID || '55b468a7-1313-47f1-a25f-a6f05837196c';
};

const msalConfig: Configuration = {
  auth: {
    clientId: getClientId(),
    authority: `https://login.microsoftonline.com/${getTenantId()}`,
    redirectUri: getRedirectUri(),
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  }
};

// Initialize MSAL
const msalInstance = new PublicClientApplication(msalConfig);

// Initialize auth service with the MSAL instance
const authService = getAuthService(msalInstance);

// Make SharePoint service available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).sharePointService = getSharePointService(authService);
}

// Initialize SharePoint connection
const SHAREPOINT_SITE_URL = process.env.REACT_APP_SHAREPOINT_SITE_URL || 'https://netorgft17318468.sharepoint.com/sites/TrueCareClinicVolunteers';
initializeSP(SHAREPOINT_SITE_URL);

// Authentication Guard Component
const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Login Route Component
const LoginRoute: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // If already authenticated, redirect to home
  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  return <Login />;
};

// Main App Component with proper authentication
function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <ThemeProvider>
        <AuthProvider>
          <SharePointProvider sharePointService={getSharePointService(authService)}>
            <Router>
              <div className="App">
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<LoginRoute />} />
                  
                  {/* Protected routes - require authentication */}
                  <Route path="/" element={
                    <AuthGuard>
                      <ProtectedRoute allowedRoles={['admin', 'executive', 'manager', 'scheduler', 'onboarding', 'volunteer']}>
                        <Layout />
                      </ProtectedRoute>
                    </AuthGuard>
                  }>
                    <Route index element={<Navigate to="/home" replace />} />
                    <Route path="home" element={<Home />} />
                    <Route path="volunteers" element={<VolunteerRoster />} />
                    <Route path="volunteer-list" element={<VolunteerList />} />
                    <Route path="patient-care" element={<PatientCare />} />
                    <Route path="org-chart" element={<OrgChart />} />
                    <Route path="calendar" element={<CalendarPage />} />

                    <Route path="prayer-test" element={<PrayerCommentBoxDemo />} />
                    <Route path="tasks" element={<TaskManagement />} />
                    <Route path="documents" element={
                      <ProtectedRoute allowedRoles={['admin', 'executive']}>
                        <DocumentManagement />
                      </ProtectedRoute>
                    } />
                  </Route>
                  
                  {/* Catch all - redirect to home if authenticated, login if not */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </div>
            </Router>
          </SharePointProvider>
        </AuthProvider>
      </ThemeProvider>
    </MsalProvider>
  );
}

export default App;
